<template>
    <div :class="$style.wrapper">
        <div
            :class="$style.block"
            :data-interface-section="props.interfaceTarget"
            @scroll="scrollStore.onVerticalScroll"
        >
            <div :class="$style.scroller">
                <GameCard
                    v-for="(item, index) in gamesWithPlaceholders"
                    :key="index"
                    :game="item"
                    :classForGame="$style.game_card_override"
                    :iconSrc="item?.icon"
                    :iconImageClass="$style.iconImage"
                    :imageClass="$style.image"
                    :disableMicrodata="props.disableMicrodata"
                    appearance="medium_horizontal"
                    withBadges
                />
            </div>
        </div>
        <div :class="[$style.shadow, $style.shadow_top, { [$style.visible]: topActive }]" />
        <div :class="[$style.shadow, $style.shadow_bottom, { [$style.visible]: bottomActive }]" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import GameCard from '@/components/GameCard/GameCard.vue'
import { useScroll } from '@/composables/use-scroll'
import { DEFAULT_RECOMMENDATION_SIZE } from '@/constants/general'
import type { ShortGameType } from '@/types'

const props = defineProps<{
    games: ShortGameType[] | undefined
    interfaceTarget: string
    disableMicrodata?: boolean
}>()

const gamesWithPlaceholders = computed(() =>
    props.games?.length ? props.games : Array(DEFAULT_RECOMMENDATION_SIZE).fill(null),
)
const scrollStore = useScroll()
const { topActive, bottomActive } = scrollStore
</script>

<style module>
.wrapper {
    margin: -3px 0 0 -3px;
    position: relative;
    min-height: 0;
}

.block {
    scrollbar-width: none;
    overflow-y: auto;
    height: 100%;
}

.block::-webkit-scrollbar {
    display: none;
}

.block .scroller {
    margin: 3px 0 0 3px;
    display: grid;
    grid-auto-flow: row;
    grid-auto-columns: 100%;
    scroll-snap-type: y mandatory;
    gap: 8px;
}

.shadow {
    position: absolute;
    left: 0;
    right: 0;
    height: 24px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.13s ease;
}

.shadow.visible {
    opacity: 1;
}

.shadow_top {
    top: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, #000 100%);
}

.shadow_bottom {
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 100%);
}

@media (--desktop-small-plus) {
    .game_card_override {
        width: auto;
        scroll-snap-align: start;
    }
}

.image {
    display: none;
}

.iconImage {
    display: block;
}

.game_card_override {
    aspect-ratio: 1;
}

@media (--desktop-medium-plus) {
    .image {
        display: block;
    }

    .iconImage {
        display: none;
    }

    .game_card_override {
        aspect-ratio: var(--thumbnail-aspect-ratio);
    }
}

@media (--desktop-xlarge-plus) {
    .block .scroller {
        grid-template-columns: 1fr 1fr;
        grid-auto-rows: auto;
    }
}
</style>
